import { Logger } from '@utils/Logger'
import { normalizeApiError } from './errorNormalizer'

// Utility to parse API error responses and create user-friendly messages
export const parseApiErrorResponse = (errorText: string, status: number, logger: Logger): string => {
  try {
    // Handle empty response body
    logger.debug('Checking error text:', {
      errorText,
      errorTextLength: errorText?.length,
      errorTextTrimmed: errorText?.trim(),
      isEmpty: !errorText || errorText.trim() === '',
      status,
    })

    if (!errorText || errorText.trim() === '') {
      logger.debug('Empty error response body, using status-based message', { status })

      // Log a warning about empty error responses
      logger.warn('Received empty error response body from service - this may indicate a backend configuration issue where error details are not being returned')

      // Provide user-friendly messages for common HTTP status codes
      const statusMessages: Record<number, string> = {
        400: 'Authentication failed. The service could not process your request. This may be due to invalid credentials, account issues, or the account not being found in our system. Please contact support for assistance.',
        401: 'Authentication failed. Please check your username and password.',
        403: 'Access denied. Your account may not have permission to access this application.',
        404: 'Authentication service not found. Please try again later.',
        429: 'Too many login attempts. Please wait a moment and try again.',
        500: 'Authentication service error. Please try again later.',
        502: 'Authentication service temporarily unavailable. Please try again later.',
        503: 'Authentication service is currently unavailable. Please try again later.',
      }

      const statusMessage = statusMessages[status] || `Authentication failed (${status})`
      logger.debug('Returning status-based message:', statusMessage)
      return statusMessage
    }

    // Try to parse the error response as JSON
    const errorResponse = JSON.parse(errorText)

    // Support new error structure from Mkback
    // TODO: Standardize this as part of the Error Handling epic
    if (errorResponse.errorCode && (errorResponse.errorMessage || errorResponse.error)) {
      logger.debug('New Mako standard error detected:', {
        code: errorResponse.errorCode,
        message: errorResponse.errorMessage,
        status,
      })
      return errorResponse.errorMessage || errorResponse.error
    }

    // Log the original error response structure for debugging
    logger.debug('Original error response structure:', {
      errorResponse,
      hasErrors: !!errorResponse.errors,
      errorsLength: errorResponse.errors?.length,
      firstErrorBusinessCode: errorResponse.errors?.[0]?.businessSystemCode,
      firstErrorCode: errorResponse.errors?.[0]?.code,
      firstErrorMessage: errorResponse.errors?.[0]?.message,
    })

    const normalizedError = normalizeApiError(errorResponse)

    // Log the normalized error structure for debugging
    logger.debug('Normalized error structure:', {
      normalizedError,
      hasErrors: !!normalizedError.errors,
      errorsLength: normalizedError.errors?.length,
      firstErrorCode: normalizedError.errors?.[0]?.code,
      firstErrorMessage: normalizedError.errors?.[0]?.message,
    })

    // Check for specific business system codes and provide user-friendly messages
    if (normalizedError.errors && normalizedError.errors.length > 0) {
      const firstError = normalizedError.errors[0]

      // Map specific business system codes to user-friendly messages
      const businessSystemCodeMap: Record<string, string> = {
        API_LOGIN_001: 'No account found for this user. Please contact support or try a different account.',
        API_LOGIN_002: 'Account access is temporarily restricted. Please try again later.',
        API_LOGIN_003: 'Invalid credentials provided. Please check your username and password.',
        API_LOGIN_004: 'Account is locked due to multiple failed attempts. Please contact support.',
        API_LOGIN_005: 'Account verification required. Please check your email for verification instructions.',
        // Additional business error codes that might be returned
        API_LOGIN_006: 'Account is inactive. Please contact support to reactivate your account.',
        API_LOGIN_007: 'Account access has expired. Please contact support for assistance.',
        API_LOGIN_008: 'Multiple accounts found for this user. Please contact support for assistance.',
        API_LOGIN_009: 'Account requires additional verification. Please contact support.',
        API_LOGIN_010: 'Account is suspended. Please contact support for assistance.',
      }

      // First try to get the business system code from the normalized error
      const businessCode = firstError.code

      // If no business code in normalized error, try to extract from original response
      if (!businessCode && errorResponse.errors && errorResponse.errors.length > 0) {
        const originalError = errorResponse.errors[0]
        if (originalError.businessSystemCode) {
          // Use the business system code mapping if available
          const userMessage = businessSystemCodeMap[originalError.businessSystemCode]
          if (userMessage) {
            logger.debug('Parsed API error response using businessSystemCode:', {
              businessSystemCode: originalError.businessSystemCode,
              originalMessage: originalError.message,
              userMessage,
              status,
            })
            return userMessage
          }
        }
      }

      // Use mapped message if available, otherwise use the error message
      const userMessage = businessCode ? businessSystemCodeMap[businessCode] : null
      const finalMessage = userMessage || firstError.message || normalizedError.message

      logger.debug('Parsed API error response:', {
        businessSystemCode: businessCode,
        originalMessage: firstError.message,
        userMessage: finalMessage,
        status,
      })

      return finalMessage
    }

    // Fallback to normalized error message
    const fallbackMessage = normalizedError.message || 'Authentication failed'
    logger.debug('Using fallback message:', {
      normalizedErrorMessage: normalizedError.message,
      fallbackMessage,
    })
    return fallbackMessage
  } catch (parseError) {
    logger.warn('Failed to parse API error response:', { errorText, parseError })

    // If it's a JSON parsing error, provide a status-based message
    if (parseError instanceof SyntaxError) {
      logger.debug('JSON parsing error detected, using status-based message')
      const statusMessages: Record<number, string> = {
        400: 'Invalid request. Please check your credentials and try again.',
        401: 'Authentication failed. Please check your username and password.',
        403: 'Access denied. Your account may not have permission to access this application.',
        404: 'Authentication service not found. Please try again later.',
        429: 'Too many login attempts. Please wait a moment and try again.',
        500: 'Authentication service error. Please try again later.',
        502: 'Authentication service temporarily unavailable. Please try again later.',
        503: 'Authentication service is currently unavailable. Please try again later.',
      }

      const syntaxErrorMessage = statusMessages[status] || `Authentication failed (${status})`
      logger.debug('Returning syntax error status message:', syntaxErrorMessage)
      return syntaxErrorMessage
    }

    const catchFallbackMessage = `Authentication failed (${status})`
    logger.debug('Using catch block fallback message:', catchFallbackMessage)
    return catchFallbackMessage
  }
}
